import React, { useState } from "react";
import {
  Modal,
  Row,
  Col,
  Button,
  Typography,
  Space,
  message,
  Select,
  Checkbox,
} from "antd";
import { IoCloseSharp } from "react-icons/io5";
import { getAgencyFilters, getProjectFilters } from "../../helpers";
import { useDispatch, useSelector } from "react-redux";
import { actions } from "../../slice";
import * as selectors from "../../selectors";
import SealsSection from "./components/SealsSection";
import BoxesSection from "./components/BoxesSection";
import StickersSection from "./components/StickersSection";
import DevicesSection from "./components/DevicesSection";

const { Title, Text } = Typography;

// Asset type options for selection
const ASSET_TYPES = [
  { key: "seals", label: "Seals", icon: "🔒" },
  { key: "boxes", label: "Boxes", icon: "📦" },
  { key: "stickers", label: "Stickers", icon: "🏷️" },
  { key: "devices", label: "Devices", icon: "📱" },
];

const CreateKit = ({
  isCreateKitModalVisible,
  setIsCreateKitModalVisible,
  setIsAssignSamplerModalVisible,
  fetchKitLists,
}) => {
  const dispatch = useDispatch();
  const projects = useSelector(selectors.selectProjects);
  const agencies = useSelector(selectors.selectAgencies);
  let sealTypes = useSelector(selectors.selectSampleSealTypes);
  const [selectedProject, setSelectedProject] = useState("");

  const [selectedAgency, setSelectedAgency] = useState("");
  const [isKitSealsCreating, setIsKitSealsCreating] = useState(false);
  const [sealState, setSealState] = useState({
    prefix: "",
    startRange: "",
    endRange: "",
    manual: "",
    seals: [],
    type: "",
  });

  const [boxState, setBoxState] = useState({
    prefix: "",
    startRange: "",
    endRange: "",
    manual: "",
    boxes: [],
  });

  const [stickerState, setStickerState] = useState({
    prefix: "",
    startRange: "",
    endRange: "",
    manual: "",
    stickers: [],
  });

  const [deviceState, setDeviceState] = useState({
    deviceName: "",
    deviceModel: "",
    uniqueId: "",
    description: "",
    devices: [],
  });

  // Visibility states for showing/hiding sections
  const [visibleSections, setVisibleSections] = useState({
    seals: false,
    boxes: false,
    stickers: false,
    devices: false,
  });

  // update seal
  sealTypes =
    sealTypes?.filter(
      (st) =>
        st.label === "Lab Sample Seal" || st.label === "Reference Sample Seal"
    ) || []; // hard coding as per current requirement, can be dynamic if more seal types are added in future

  // Helper functions to update object states
  const updateSealState = (updates) =>
    setSealState((prev) => ({ ...prev, ...updates }));
  const updateBoxState = (updates) =>
    setBoxState((prev) => ({ ...prev, ...updates }));
  const updateStickerState = (updates) =>
    setStickerState((prev) => ({ ...prev, ...updates }));
  const updateDeviceState = (updates) =>
    setDeviceState((prev) => ({ ...prev, ...updates }));

  const toggleSection = (section) => {
    const isCurrentlyVisible = visibleSections[section];
    if (isCurrentlyVisible) {
      switch (section) {
        case "seals": 
          setSealState({
            prefix: "",
            startRange: "",
            endRange: "",
            manual: "",
            seals: [],
            type: "",
          });
          break;
        case "boxes":
          setBoxState({
            prefix: "",
            startRange: "",
            endRange: "",
            manual: "",
            boxes: [],
          });
          break;
        case "stickers":
          setStickerState({
            prefix: "",
            startRange: "",
            endRange: "",
            manual: "",
            stickers: [],
          });
          break;
        case "devices":
          setDeviceState({
            deviceName: "",
            deviceModel: "",
            uniqueId: "",
            description: "",
            devices: [],
          });
          break;
        default:
          break;
      }
    }
    setVisibleSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleKitCreationError = ({
    error,
    sealItems,
    boxItems,
    stickerItems,
    deviceItems,
  }) => {
    setIsKitSealsCreating(false);

    // Calculate total seal count from sealItems
    const totalSealCount = sealItems.reduce(
      (total, group) => total + (group.sealNumbers?.length || 0),
      0
    );
    // Calculate total failed seal count from error response
    const totalFailedSealCount =
      error?.data?.failedSealNumbers?.reduce(
        (total, group) => total + (group.count || 0),
        0
      ) || 0;

    const allSealsFailed =
      totalFailedSealCount === totalSealCount && totalFailedSealCount > 0;
    const allBoxesFailed =
      error?.data?.failedBoxNumbers?.length === boxItems?.length &&
      error?.data?.failedBoxNumbers?.length > 0;
    const allStickersFailed =
      error?.data?.failedStickerNumbers?.length === stickerItems?.length &&
      error?.data?.failedStickerNumbers?.length > 0;
    const allDevicesFailed =
      error?.data?.failedDevices?.length === deviceItems?.length &&
      error?.data?.failedDevices?.length > 0;

    // Build error message based on what failed
    const failedAssets = [];
    if (allSealsFailed) failedAssets.push("Seal");
    if (allBoxesFailed) failedAssets.push("Box");
    if (allStickersFailed) failedAssets.push("Sticker");
    if (allDevicesFailed) failedAssets.push("Device");

    let errorMessage = error?.message || "Failed to create kit";
    if (failedAssets.length > 0) {
      errorMessage = `Oops, These ${failedAssets.join(" and ")} ids already taken, Please make unique ids`;
    }

    message.error(errorMessage, 6);
  };

  // kit creation with the Assets
  const handleCreateKitSeal = () => {
    const { seals, boxes, stickers, devices } = visibleSections;
    const sealItems = sealState.seals;
    const boxItems = boxState.boxes;
    const stickerItems = stickerState.stickers;
    const deviceItems = deviceState.devices;

    if (!selectedProject || !selectedAgency) {
      message.error("Please select a project and agency");
      return;
    }
    if (!seals && !boxes && !stickers && !devices) {
      message.error("Please select at least one asset type");
      return;
    }
    if (seals) {
      const totalSealCount = sealItems.reduce(
        (total, group) => total + (group.sealNumbers?.length || 0),
        0
      );
      if (totalSealCount === 0) {
        message.error("Add at least one seal or deselect Seals");
        return;
      }
    }
    if (boxes && boxItems.length === 0) {
      message.error("Add at least one box or deselect Boxes");
      return;
    }
    if (stickers && stickerItems.length === 0) {
      message.error("Add at least one sticker or deselect Stickers");
      return;
    }
    if (devices && deviceItems.length === 0) {
      message.error("Add at least one device or deselect Devices");
      return;
    }
    // Call API to create kit with selected project, agency, seals, boxes, stickers, and devices
    const postData = {
      projectCodeId: selectedProject,
      agencyId: selectedAgency,
      sampleSeals: sealItems,
      samplerBoxNumbers: boxItems,
      sampleStickerNumbers: stickerItems,
      devices: deviceItems,
    };
    setIsKitSealsCreating(true);
    dispatch(
      actions.createKitSeals({
        postData,
        onSuccess: () => {
          setIsAssignSamplerModalVisible(true);
          fetchKitLists();
          resetState();
          message.success("Kit created successfully");
        },
        onFailure: (error) => {
          handleKitCreationError({
            error,
            sealItems,
            boxItems,
            stickerItems,
            deviceItems,
          });
        },
      })
    );
  };

  const resetState = () => {
    setSealState({
      prefix: "",
      startRange: "",
      endRange: "",
      manual: "",
      seals: [],
      type: "",
    });

    setBoxState({
      prefix: "",
      startRange: "",
      endRange: "",
      manual: "",
      boxes: [],
    });

    setStickerState({
      prefix: "",
      startRange: "",
      endRange: "",
      manual: "",
      stickers: [],
    });

    setDeviceState({
      deviceName: "",
      deviceModel: "",
      uniqueId: "",
      description: "",
      devices: [],
    });

    setVisibleSections({
      seals: false,
      boxes: false,
      stickers: false,
      devices: false,
    });

    setSelectedProject("");
    setSelectedAgency("");
    setIsKitSealsCreating(false);
    setIsCreateKitModalVisible(false);
  };

  // Calculate total assets for footer
  const getTotalSealCount = () =>
    sealState.seals.reduce(
      (total, group) => total + (group.sealNumbers?.length || 0),
      0
    );

  const totalAssets =
    getTotalSealCount() +
    boxState.boxes.length +
    stickerState.stickers.length +
    deviceState.devices.length;

  // Footer
  const footer = (
    <Row justify="end" style={{ marginTop: 0 }}>
      <Space size={16}>
        <Button
          size="large"
          onClick={resetState}
          style={{
            borderRadius: 8,
            fontWeight: 700,
            border: "1px solid #D9D9D9",
            background: "#fff",
          }}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          size="large"
          style={{
            borderRadius: 8,
            fontWeight: 700,
            background: "#23235F",
            color: "#fff",
          }}
          loading={isKitSealsCreating}
          onClick={handleCreateKitSeal}
        >
          Create Kit{" "}
          {totalAssets
            ? `with ${totalAssets} Asset${totalAssets > 1 ? "s" : ""}`
            : "Asset"}
        </Button>
      </Space>
    </Row>
  );

  return (
    <>
      <Modal
        visible={isCreateKitModalVisible}
        onCancel={resetState}
        title={
          <Title level={3} style={{ fontWeight: 800, marginBottom: 0 }}>
            Create New Sampling Kit
          </Title>
        }
        footer={footer}
        width={900}
        bodyStyle={{
          borderRadius: 16,
          padding: 24,
          background: "#fff",
          overflowX: "auto",
          maxHeight: "70vh",
          overflowY: "auto",
        }}
        style={{ borderRadius: 16 }}
        closeIcon={<IoCloseSharp size="24" />}
        centered
        className="modal-content"
      >
        <Row gutter={[20, 12]} style={{ marginBottom: 15 }}>
          <Col span={12}>
            <Text style={{ fontWeight: 600, color: "#232A36" }}>
              Project Code <span style={{ color: "red" }}>*</span>
            </Text>
            <Select
              showSearch
              optionFilterProp="label"
              defaultValue="All Projects"
              placeholder="All Projects"
              options={getProjectFilters(projects)}
              value={selectedProject || "All Projects"}
              onChange={(value) => setSelectedProject(value)}
              className="filter-select"
              style={{
                width: "100%",
                marginTop: 6,
              }}
            />
          </Col>
          <Col span={12}>
            <Text style={{ fontWeight: 600, color: "#232A36" }}>
              Agency <span style={{ color: "red" }}>*</span>
            </Text>
            <Select
              showSearch
              optionFilterProp="label"
              defaultValue="All Agencies"
              placeholder="All Agencies"
              options={getAgencyFilters(agencies)}
              value={selectedAgency || "All Agencies"}
              onChange={(value) => setSelectedAgency(value)}
              className="filter-select"
              style={{
                width: "100%",
                marginTop: 6,
              }}
            />
          </Col>
        </Row>

        {/* Asset Type Selection - Checkboxes */}
        <div style={{ marginBottom: 16 }}>
          <Row justify="space-between" align="middle">
            <Text style={{ fontWeight: 700, fontSize: 14, color: "#232A36" }}>
              Select Asset Types <span style={{ color: "red" }}>*</span>
            </Text>
            <Space size={12}>
              {ASSET_TYPES.map(({ key, label, icon }) => (
                <div
                  key={key}
                  onClick={() => toggleSection(key)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 14px",
                    borderRadius: 8,
                    border: visibleSections[key]
                      ? "2px solid #23235F"
                      : "1px solid #E5E8EC",
                    background: visibleSections[key] ? "#F0F0FF" : "#fff",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  <Checkbox
                    checked={visibleSections[key]}
                    style={{ pointerEvents: "none" }}
                  />
                  <span style={{ fontSize: 16 }}>{icon}</span>
                  <Text
                    style={{ fontWeight: 600, fontSize: 13, color: "#232A36" }}
                  >
                    {label}
                  </Text>
                </div>
              ))}
            </Space>
          </Row>
        </div>

        {/* Seals Section */}
        {visibleSections.seals && (
          <SealsSection
            sealState={sealState}
            updateSealState={updateSealState}
            sealTypes={sealTypes}
          />
        )}

        {/* Boxes Section */}
        {visibleSections.boxes && (
          <BoxesSection boxState={boxState} updateBoxState={updateBoxState} />
        )}

        {/* Stickers Section */}
        {visibleSections.stickers && (
          <StickersSection
            stickerState={stickerState}
            updateStickerState={updateStickerState}
          />
        )}

        {/* Devices Section */}
        {visibleSections.devices && (
          <DevicesSection
            deviceState={deviceState}
            updateDeviceState={updateDeviceState}
          />
        )}
      </Modal>
    </>
  );
};

export default CreateKit;
